import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { uploadManagedFile } from "@/utils/managedFile";

/**
 * Kampanie mailowe.
 *
 * Wysyłką zajmuje się dyspozytor w tle — front tylko przygotowuje treści,
 * zatwierdza je i pokazuje stan kolejki. Dlatego nie ma tu żadnego „wyślij teraz":
 * limit dzienny i okno wysyłki są po stronie serwera i nie da się ich obejść z UI.
 */

export type CampaignStatus = "draft" | "running" | "paused" | "done";
export type MessageStatus = "draft" | "approved" | "sent" | "failed" | "cancelled";
export type RecipientStatus = "active" | "finished" | "stopped";

export interface CampaignCounts {
  recipients: number;
  draft: number;
  queued: number;
  sent: number;
  failed: number;
  cancelled: number;
  stopped: number;
  /** Odpowiedzi czekające na obsługę w skrzynce zakupy@ — badge na karcie. */
  replies: number;
}

/** Format treści kampanii: zwykły tekst (markdown renderowany przy wysyłce)
 *  albo HTML z edytora wizualnego, wysyłany bez konwersji. */
export type CampaignBodyFormat = "markdown" | "html";

export interface CampaignAttachment {
  id: string;
  managed_file: string;
  original_filename: string;
  size_bytes: number | null;
  url: string | null;
  created_at: string;
}

export interface Campaign {
  id: string;
  name: string;
  job_description: string;
  subject_template: string;
  body_format: CampaignBodyFormat;
  body_template: string;
  follow_up_template: string;
  follow_up_days: number[];
  status: CampaignStatus;
  status_label: string;
  needs_personalization: boolean;
  attachments: CampaignAttachment[];
  counts: CampaignCounts;
  started_at: string | null;
  finished_at: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface CampaignMessage {
  id: string;
  recipient: string;
  lead: string;
  lead_name: string;
  email: string;
  contact_name: string | null;
  recipient_status: RecipientStatus;
  stop_reason: string;
  step: number;
  subject: string;
  body: string;
  personalization: string;
  status: MessageStatus;
  status_label: string;
  scheduled_at: string | null;
  sent_at: string | null;
  error: string;
  cost_usd: string;
}

export interface QueueState {
  sending_enabled: boolean;
  mailbox: string;
  daily_cap: number;
  sent_today: number;
  remaining_today: number;
  queued: number;
  within_window: boolean;
  window: string;
  days_to_drain: number;
  /** Kiedy wyjdzie następny mail — momenty są losowane na starcie dnia. */
  next_send_at: string | null;
  slots_today: number;
  sent_since_midnight: number;
  /** Ile momentów wysyłki zostało do końca dnia — realny sufit na dziś. */
  slots_left_today: number;
}

const KEY = ["leads", "campaigns"];

export function useCampaigns() {
  return useQuery({
    queryKey: KEY,
    queryFn: () =>
      apiClient.get<{ data: Campaign[] }>("/leads/campaigns/").then((r) => r.data.data),
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, id],
    enabled: !!id,
    queryFn: () =>
      apiClient.get<{ data: Campaign }>(`/leads/campaigns/${id}/`).then((r) => r.data.data),
  });
}

export function useCampaignMessages(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, id, "messages"],
    enabled: !!id,
    queryFn: () =>
      apiClient
        .get<{ data: CampaignMessage[] }>(`/leads/campaigns/${id}/recipients/`)
        .then((r) => r.data.data),
    // Personalizacja liczy się w tle — bez odświeżania użytkownik patrzy na puste
    // akapity i nie wie, czy to już koniec, czy jeszcze trwa.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((m) => m.status === "draft" && !m.personalization)
        ? 5000
        : false,
  });
}

/**
 * Wiersz globalnej kolejki wysyłki — bez treści maila; ten ekran odpowiada na
 * „kto jest następny", a nie „co do niego napiszemy".
 */
export interface QueuedMessage {
  id: string;
  lead: string;
  lead_name: string;
  email: string;
  contact_name: string;
  campaign: string;
  campaign_name: string;
  step: number;
  subject: string;
  status: MessageStatus;
  status_label: string;
  /* Termin: OD KIEDY wolno wysłać. Przy przypomnieniach bierze się z godziny
     poprzedniej wysyłki, więc terminy siedzą blisko siebie — to nie znaczy, że
     maile wyjdą blisko siebie. Od „kiedy wyjdzie" jest projected_send_at. */
  scheduled_at: string | null;
  /* Przewidywany moment wysyłki: dziś z wylosowanego planu dnia, w kolejnych dniach
     szacunkowo (plany na przyszłe dni jeszcze nie istnieją). null = wysyłka
     wyłączona albo kolejka dłuższa niż horyzont projekcji. */
  projected_send_at: string | null;
  queue_order: number | null;
  /* Czy termin już nadszedł. Wiersze z terminem w przyszłości wiszą pod kreską —
     nie zajmują dzisiejszych momentów wysyłki i niczego nie blokują. */
  is_due: boolean;
}

export function useQueueMessages() {
  return useQuery({
    queryKey: [...KEY, "queue", "messages"],
    queryFn: () =>
      apiClient
        .get<{ data: QueuedMessage[] }>("/leads/campaigns/queue/messages/")
        .then((r) => r.data.data),
    // Kolejka schodzi w tle co 10 minut — bez odświeżania ekran pokazywałby maile,
    // które już wyszły, i kusił do przestawiania czegoś, czego nie ma.
    refetchInterval: 30000,
  });
}

/** Zapisuje kolejność w takiej postaci, w jakiej widać ją na ekranie po upuszczeniu. */
export function useReorderQueue() {
  return useCampaignMutation((messageIds: string[]) =>
    apiClient
      .post<{ data: { reordered: number; messages: QueuedMessage[] } }>(
        "/leads/campaigns/queue/reorder/",
        { messages: messageIds },
      )
      .then((r) => r.data.data),
  );
}

/**
 * Mail kontrolny na sprawdzarkę (mail-tester.com) albo własną skrzynkę — ta sama
 * treść i ta sama droga co realna kampania, żeby ocena dotyczyła tego, co
 * naprawdę dostają firmy.
 */
export function useTestSend() {
  return useCampaignMutation(({ id, email }: { id: string; email: string }) =>
    apiClient
      .post<{ data: { address: string; subject: string; sample_company: string } }>(
        `/leads/campaigns/${id}/test-send/`,
        { email },
      )
      .then((r) => r.data.data),
  );
}

export interface SpamCheck {
  id: string;
  test_id: string;
  address: string;
  status: "pending" | "done" | "failed";
  status_label: string;
  score: string | null;
  summary: string;
  issues: { code: string; score: number; detail: string }[];
  auth: Record<string, boolean>;
  sample_company: string;
  needs_attention: boolean;
  checked_at: string | null;
  error: string;
  created_at: string;
}

/** Kontrola antyspamowa treści — odpala się sama, tu tylko czytamy wynik. */
export function useSpamChecks(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, id, "spam-check"],
    enabled: !!id,
    queryFn: () =>
      apiClient
        .get<{ data: { enabled: boolean; checks: SpamCheck[] } }>(
          `/leads/campaigns/${id}/spam-check/`,
        )
        .then((r) => r.data.data),
    // Wynik przychodzi kilka minut po wysłaniu badania — bez odświeżania ekran
    // pokazywałby „czeka" do następnego wejścia.
    refetchInterval: (query) =>
      (query.state.data?.checks ?? []).some((c) => c.status === "pending") ? 30000 : false,
  });
}

export function useRunSpamCheck() {
  return useCampaignMutation((id: string) =>
    apiClient.post(`/leads/campaigns/${id}/spam-check/`, {}),
  );
}

export function useQueueState() {
  return useQuery({
    queryKey: [...KEY, "queue"],
    queryFn: () =>
      apiClient.get<{ data: QueueState }>("/leads/campaigns/queue/").then((r) => r.data.data),
    refetchInterval: 30000,
  });
}

function useCampaignMutation<TArgs, TResult = unknown>(
  fn: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });
}

export interface CampaignPayload {
  name: string;
  job_description: string;
  subject_template: string;
  body_format: CampaignBodyFormat;
  body_template: string;
  follow_up_template: string;
  follow_up_days: number[];
}

export function useSaveCampaign() {
  return useCampaignMutation(({ id, ...payload }: CampaignPayload & { id?: string }) =>
    id
      ? apiClient.patch<{ data: Campaign }>(`/leads/campaigns/${id}/`, payload).then((r) => r.data.data)
      : apiClient.post<{ data: Campaign }>("/leads/campaigns/", payload).then((r) => r.data.data),
  );
}

export function useDeleteCampaign() {
  return useCampaignMutation((id: string) => apiClient.delete(`/leads/campaigns/${id}/`));
}

/** Załącznik idzie z KAŻDYM mailem kampanii — dwustopniowo jak pliki leada:
 *  najpierw generyczny upload, potem podpięcie do kampanii. */
export function useUploadCampaignAttachment() {
  return useCampaignMutation(async ({ campaignId, file }: { campaignId: string; file: File }) => {
    const uploaded = await uploadManagedFile(file);
    return apiClient.post(`/leads/campaigns/${campaignId}/attachments/`, {
      managed_file: uploaded.id,
    });
  });
}

export function useDeleteCampaignAttachment() {
  return useCampaignMutation((id: string) =>
    apiClient.delete(`/leads/campaign-attachments/${id}/`),
  );
}

export interface AddRecipientsOutcome {
  added: { lead: string; name: string }[];
  skipped: { lead: string; name: string; reason: string }[];
}

export function useAddRecipients() {
  return useCampaignMutation(({ id, leadIds }: { id: string; leadIds: string[] }) =>
    apiClient
      .post<{ data: AddRecipientsOutcome }>(`/leads/campaigns/${id}/recipients/`, {
        lead_ids: leadIds,
      })
      .then((r) => r.data.data),
  );
}

/**
 * Cofa zatrzymanie sekwencji dla jednej firmy w tej kampanii.
 *
 * Odzyskany mail wraca do zatwierdzenia, a nie prosto do kolejki — po fałszywej
 * „odpowiedzi" ktoś ma zobaczyć, co dokładnie po niej wyjdzie.
 */
export function useResumeRecipient() {
  return useCampaignMutation(({ id, recipient }: { id: string; recipient: string }) =>
    apiClient.post(`/leads/campaigns/${id}/resume-recipient/`, { recipient }),
  );
}

export interface PersonalizeOutcome {
  queued: number;
  message: string;
}

/**
 * Bez `force` przelicza tylko brakujące akapity — powtórne kliknięcie nie płaci
 * drugi raz za to samo i nie kasuje poprawek wpisanych ręcznie.
 */
export function usePersonalizeCampaign() {
  return useCampaignMutation(({ id, force }: { id: string; force?: boolean }) =>
    apiClient
      .post<{ data: PersonalizeOutcome }>(`/leads/campaigns/${id}/personalize/`, {
        force: !!force,
      })
      .then((r) => r.data.data),
  );
}

export function useApproveMessages() {
  return useCampaignMutation(({ id, messageIds }: { id: string; messageIds: string[] }) =>
    apiClient.post(`/leads/campaigns/${id}/approve/`, { message_ids: messageIds }),
  );
}

export function useCancelMessages() {
  return useCampaignMutation(({ id, messageIds }: { id: string; messageIds: string[] }) =>
    apiClient.post(`/leads/campaigns/${id}/cancel/`, { message_ids: messageIds }),
  );
}

export function useCampaignAction() {
  return useCampaignMutation(({ id, action }: { id: string; action: "start" | "pause" }) =>
    apiClient.post(`/leads/campaigns/${id}/${action}/`, {}),
  );
}

export interface FinishOutcome {
  /** Ile maili czekających na wysyłkę anulowano (szkice + kolejka). */
  cancelled: number;
  /** Ile z nich to zaplanowane przypomnienia do firm, które nie odpisały. */
  follow_ups_cancelled: number;
  /** Ilu firmom zakończono sekwencję — żadna z nich nie dostanie już maila. */
  recipients_finished: number;
  campaign: Campaign;
}

/**
 * Zakończenie kampanii na stałe.
 *
 * Osobno od start/pause, bo to jedyna akcja kampanii, która coś ODBIERA: wyjmuje
 * z kolejki wszystko, co jeszcze czekało — także przypomnienia zaplanowane po
 * wysłanych zapytaniach — i kończy sekwencje wszystkich odbiorców. Zakończonej
 * kampanii nie da się uruchomić ponownie.
 */
export function useFinishCampaign() {
  return useCampaignMutation((id: string) =>
    apiClient
      .post<{ data: FinishOutcome }>(`/leads/campaigns/${id}/finish/`, {})
      .then((r) => r.data.data),
  );
}

export const CAMPAIGN_STATUS_COLORS: Record<CampaignStatus, "gray" | "blue" | "yellow" | "green"> = {
  draft: "gray",
  running: "blue",
  paused: "yellow",
  done: "green",
};

export const MESSAGE_STATUS_COLORS: Record<MessageStatus, "gray" | "blue" | "green" | "red"> = {
  draft: "gray",
  approved: "blue",
  sent: "green",
  failed: "red",
  cancelled: "gray",
};

export interface CampaignMessageDetail extends CampaignMessage {
  /** Treść tak, jak zobaczy ją adresat — składana tą samą funkcją co przy wysyłce. */
  body_html: string;
  can_edit: boolean;
}

export function useCampaignMessage(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "message", id],
    enabled: !!id,
    queryFn: () =>
      apiClient
        .get<{ data: CampaignMessageDetail }>(`/leads/campaign-messages/${id}/`)
        .then((r) => r.data.data),
  });
}

export function useSaveMessage() {
  return useCampaignMutation(
    ({ id, ...payload }: { id: string; subject?: string; body?: string }) =>
      apiClient
        .patch<{ data: CampaignMessageDetail }>(
          `/leads/campaign-messages/${id}/`,
          payload,
        )
        .then((r) => r.data.data),
  );
}

/** Składa treść od nowa z aktualnego szablonu kampanii — ratunek na wiadomości
 *  przygotowane przed zmianą szablonu (np. z nieistniejącym już `{{imie}}`). */
export function useRecomposeMessage() {
  return useCampaignMutation((id: string) =>
    apiClient
      .post<{ data: CampaignMessageDetail }>(
        `/leads/campaign-messages/${id}/recompose/`,
        {},
      )
      .then((r) => r.data.data),
  );
}

// ── Redakcja AI szablonu ─────────────────────────────────────────────────────

/** Styl, w jakim model ma ubrać szkic. Tekst pisze człowiek — model tylko formę. */
export type RefineMode = "standard" | "formal";

export interface RefineResult {
  body: string;
  /** false = nic nie podmieniamy (odrzucona redakcja albo brak zmian) — patrz `note`. */
  changed: boolean;
  note: string;
}

/**
 * Redakcja AI szkicu szablonu: forma, nigdy treść.
 *
 * Bez id kampanii — redagujemy to, co jest W EDYTORZE, także zanim kampania
 * powstanie. Serwer sprawdza po fakcie, że znaczniki `{{…}}` i liczby przetrwały
 * co do znaku; jeśli nie, wraca oryginał i widać dlaczego. Bez invalidacji
 * zapytań: po stronie serwera nic się nie zmieniło.
 */
export function useRefineTemplate() {
  return useMutation({
    mutationFn: (payload: { body: string; mode: RefineMode; body_format: CampaignBodyFormat }) =>
      apiClient
        .post<{ data: RefineResult }>("/leads/campaigns/refine/", payload)
        .then((r) => r.data.data),
  });
}
